import React, { useCallback } from 'react';
import { Info, LayoutGrid, List } from 'lucide-react';
import {
  Button,
  EmptySearchState,
  SearchInput,
  SixCard,
  SixListTable,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  type SixListTableItem,
} from 'shared-portal-components-v2';

import { PENDING_HAP_STATUS, type SixCatalogItem } from '@/types/Sixes';
import { getFileCountFromSix, getLastActivityLabel } from '@/utils/sixCatalog.utils';
import SixDashboardSortSelect from '@/pages/SixCatalogPage/SixDashboardSortSelect';
import type { SortKey, SortOption, ViewMode } from '@/types/SixCatalog.types';
import type { CatalogSortDirection } from '@/types/SixCatalogDTOs';
import type { ConnectionStatus } from '@/store/slices/poc/connectionSlice';

type CatalogCardConnectionStatus = ConnectionStatus | 'awaitingHap';

export interface SixCatalogPageViewProps {
  searchValue: string;
  selectedSortKey: SortKey;
  selectedSortDirection: CatalogSortDirection;
  viewMode: ViewMode;
  sortedSixes: SixCatalogItem[];
  showEmptyState: boolean;
  sortOptions: SortOption[];
  connectionStatusBySixId: Record<string, ConnectionStatus>;
  ownershipBySixId: Record<string, boolean>;

  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  onSortChange: (nextValue: string) => void;
  onSortColumnClick: (nextValue: string) => void;
  onViewModeChange: (nextValue: string) => void;
  onCardClick: (sixId: string) => void;
  onToggleFavorite: (sixId: string) => void;
  onEdit: (sixId: string) => void;
  onDelete: (sixId: string) => void;
}

interface SixCardItemProps {
  sixId: string;
  title: string;
  description: string;
  memberCount: number;
  fileCount: number;
  lastActivity: string;
  connectionStatus: CatalogCardConnectionStatus;
  isFavorite: boolean;
  isOwner: boolean;
  onCardClick: (sixId: string) => void;
  onToggleFavorite: (sixId: string) => void;
  onEdit: (sixId: string) => void;
  onDelete: (sixId: string) => void;
}

const SixCardItem = React.memo(function SixCardItem({
  sixId,
  title,
  description,
  memberCount,
  fileCount,
  lastActivity,
  connectionStatus,
  isFavorite,
  isOwner,
  onCardClick,
  onToggleFavorite,
  onEdit,
  onDelete,
}: SixCardItemProps) {
  const handleClick = useCallback(() => {
    onCardClick(sixId);
  }, [onCardClick, sixId]);
  const handleToggleFavorite = useCallback(() => {
    onToggleFavorite(sixId);
  }, [onToggleFavorite, sixId]);
  const handleEdit = useCallback(() => {
    onEdit(sixId);
  }, [onEdit, sixId]);
  const handleDelete = useCallback(() => {
    onDelete(sixId);
  }, [onDelete, sixId]);

  return (
    <SixCard
      title={title}
      description={description}
      memberCount={memberCount}
      fileCount={fileCount}
      lastActivity={lastActivity}
      connectionStatus={connectionStatus}
      isFavorite={isFavorite}
      isOwner={isOwner}
      onClick={handleClick}
      onToggleFavorite={handleToggleFavorite}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );
});

export default function SixCatalogPageView({
  searchValue,
  selectedSortKey,
  selectedSortDirection,
  viewMode,
  sortedSixes,
  showEmptyState,
  sortOptions,
  connectionStatusBySixId,
  ownershipBySixId,
  onSearchChange,
  onSearchClear,
  onSortChange,
  onSortColumnClick,
  onViewModeChange,
  onCardClick,
  onToggleFavorite,
  onEdit,
  onDelete,
}: SixCatalogPageViewProps): React.ReactElement {
  const resolveCardConnectionStatus = (
    sixId: string,
    sixStatus: SixCatalogItem['status']
  ): CatalogCardConnectionStatus => {
    if (sixStatus === PENDING_HAP_STATUS) {
      return 'awaitingHap';
    }

    return connectionStatusBySixId[sixId] ?? 'disconnected';
  };

  const listItems: SixListTableItem[] = sortedSixes.map((six) => ({
    id: six.id,
    title: six.name,
    description: six.description,
    memberCount: six.participantCount,
    fileCount: getFileCountFromSix(six),
    lastActivity: getLastActivityLabel(six.lastModified),
    connectionStatus: resolveCardConnectionStatus(six.id, six.status),
    isFavorite: six.isFavorite,
    isOwner: ownershipBySixId[six.id] ?? false,
  }));

  const handleViewModeChange = (nextValue: string | string[]): void => {
    if (typeof nextValue === 'string') {
      onViewModeChange(nextValue);
    }
  };

  return (
    <>
      <div className="flex-1 p-6">
        <div className="space-y-6">
          <div className="flex items-center">
            <h1 className="text-3xl font-bold text-foreground">SIX Dashboard</h1>
            <Button
              size="icon"
              variant="tertiary"
              className="p-0 border-0 bg-transparent hover:bg-transparent focus:outline-none"
              aria-label="About SIX Dashboard"
              onClick={() => undefined}
            >
              <Tooltip
                position="bottom"
                className="w-72"
                content="All SIXs that you own, or that you have been invited to participate in, are shown on your SIX Dashboard. If you don't see a SIX that you expect to see, please remove any filters that you have set and click your browser's refresh button."
              >
                <Info className="h-5 w-5 text-muted-foreground hover:text-foreground cursor-pointer" />
              </Tooltip>
            </Button>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-md">
              <SearchInput
                value={searchValue}
                className="flex-1 h-11"
                onChange={(value: React.SetStateAction<string>) => {
                  if (typeof value === 'string') {
                    onSearchChange(value);
                  }
                }}
                placeholder="Search for a SIX"
                onClear={onSearchClear}
              />
            </div>

            <div className="flex items-center gap-4">
              <ToggleGroup
                type="single"
                value={viewMode}
                onValueChange={handleViewModeChange}
                ariaLabel="View mode"
              >
                <ToggleGroupItem value="grid" aria-label="Grid view" className="w-10 px-0">
                  <LayoutGrid className="h-4 w-4 shrink-0" />
                </ToggleGroupItem>
                <ToggleGroupItem value="list" aria-label="List view" className="w-10 px-0">
                  <List className="h-4 w-4 shrink-0" />
                </ToggleGroupItem>
              </ToggleGroup>

              <SixDashboardSortSelect
                value={selectedSortKey}
                sortOptions={sortOptions}
                onChange={onSortChange}
              />
            </div>
          </div>

          {showEmptyState ? (
            <EmptySearchState
              title="No SIXs found"
              description="We couldn't find any SIXs matching your search. Try adjusting your filters or search query."
            />
          ) : viewMode === 'list' ? (
            <SixListTable
              items={listItems}
              activeSortKey={selectedSortKey}
              activeSortDirection={selectedSortDirection}
              onSortColumnClick={onSortColumnClick}
              onRowClick={onCardClick}
              onToggleFavorite={onToggleFavorite}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {sortedSixes.map((six) => (
                <SixCardItem
                  key={six.id}
                  sixId={six.id}
                  title={six.name}
                  description={six.description}
                  memberCount={six.participantCount}
                  fileCount={getFileCountFromSix(six)}
                  lastActivity={getLastActivityLabel(six.lastModified)}
                  connectionStatus={resolveCardConnectionStatus(six.id, six.status)}
                  isFavorite={six.isFavorite}
                  isOwner={ownershipBySixId[six.id] ?? false}
                  onCardClick={onCardClick}
                  onToggleFavorite={onToggleFavorite}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}

          <div className="hidden" />
        </div>
      </div>
    </>
  );
}
